import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";

const BUTTON = "rounded-[4px] border px-2 py-1 text-xs font-semibold focus-visible:outline-2 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400";

export default function ProductExpansionConfirmation({ anchor, policy, disabled, onClose }) {
  const titleId = React.useId();
  const cancelRef = React.useRef(null);
  const { refs, floatingStyles } = useFloating({
    placement: "left-start", strategy: "fixed",
    middleware: [offset(10), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  React.useLayoutEffect(() => { refs.setReference(anchor); cancelRef.current?.focus(); }, [anchor, refs.setReference]);
  usePopoverDismiss({ anchorElement: anchor, floatingRef: refs.floating, isOpen: true, onClose });
  const root = getReactPortalRoot();
  if (!root) return null;
  return createPortal(<div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 10000 }}
    role="dialog" aria-labelledby={titleId} data-product-expansion-confirmation="true"
    className="w-[min(360px,calc(100vw-24px))] rounded-[4px] border border-slate-300 bg-white p-3 text-sm text-slate-700 shadow-xl"
    onKeyDown={(event) => {
      event.stopPropagation();
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key === "Tab") {
        const buttons = [...event.currentTarget.querySelectorAll('button:not(:disabled)')];
        if (buttons.length) {
          event.preventDefault();
          const index = buttons.indexOf(document.activeElement);
          buttons[(index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus();
        }
      }
    }}>
    <h3 id={titleId} className="m-0 text-sm font-semibold text-slate-900">Désactiver l’expansion des produits ?</h3>
    <p className="my-2">Pour cette date, en Jour et en Soir, les prochains calculs pourront charger les produits jusqu’à la capacité des compartiments, sans réserver l’espace prévu par les pourcentages d’expansion.</p>
    <p className="my-2">Les règles restent enregistrées. Les tournées actuelles ne sont pas recalculées automatiquement. Les autres contraintes de capacité restent applicables.</p>
    {policy.error ? <p role="alert" className="my-2 text-xs text-red-700">{policy.error}</p> : null}
    <div className="mt-3 flex justify-end gap-2">
      <button ref={cancelRef} type="button" className={`${BUTTON} border-slate-300 bg-white hover:bg-slate-50`} onClick={onClose}>Annuler</button>
      <button type="button" disabled={disabled || policy.isSaving || !policy.available}
        className={`${BUTTON} border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong`}
        onClick={async () => { if (await policy.save(false)) onClose(); }}>
        {policy.isSaving ? "Enregistrement…" : "Désactiver"}
      </button>
    </div>
  </div>, root);
}
